// Certificados médicos (Centro Médico → Clínica → Certificados).
// Reposo / aptitud / asistencia / general, con código de verificación (QR),
// firma del médico (snapshot) y firma del paciente (canvas). Impresión con print().
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Ban, FileBadge, Loader2, Plus, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import { useAuth } from '@/lib/hooks';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { FirmaCanvas } from './FirmaCanvas';
import { DiagnosticosCie } from './DiagnosticosCie';
import {
  AutoTextarea,
  Campo, CertificadoDetalle, CertificadoRow, CONTINGENCIAS_REPOSO, MisionBadge, Modal, PacienteRow,
  TIPOS_CERTIFICADO, cantidadEnLetras, fechaEnLetras, fieldCls, medRpc,
} from './shared';
import type { Diagnostico } from './shared';
import { LOCALE } from '@/lib/utils/regional';

const tipoLabel = (v: string) => TIPOS_CERTIFICADO.find((t) => t.v === v)?.label ?? v;

export function Certificados() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [pacienteId, setPacienteId] = useState('');
  const { data: pacientes = [] } = useQuery<PacienteRow[]>({ queryKey: ['med-pacientes', ''], queryFn: () => medRpc<PacienteRow[]>('med_pacientes', { _busqueda: '' }) });
  const { data: certs = [], isFetching } = useQuery<CertificadoRow[]>({
    queryKey: ['med-certificados', pacienteId], queryFn: () => medRpc<CertificadoRow[]>('med_certificados', { _paciente_id: pacienteId }), enabled: !!pacienteId,
  });

  const [nuevo, setNuevo] = useState(false);
  const [imprimir, setImprimir] = useState<string | null>(null);
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['med-certificados', pacienteId] });

  const anular = useMutation({
    mutationFn: (id: string) => medRpc('med_anular_certificado', { _id: id, _motivo: 'Anulado por el médico' }),
    onSuccess: () => { toast.success('Certificado anulado'); refrescar(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><FileBadge className="w-5 h-5" /> Certificados</h1>
        <div className="flex items-center gap-2">
          <select className={`${fieldCls} w-72`} value={pacienteId} onChange={(e) => setPacienteId(e.target.value)}>
            <option value="">— Selecciona un paciente —</option>
            {pacientes.map((p) => <option key={p.id} value={p.id}>{p.razon_social} ({p.identificacion ?? 's/i'})</option>)}
          </select>
          {pacienteId && <Button onClick={() => setNuevo(true)}><Plus className="w-4 h-4 mr-1" /> Nuevo certificado</Button>}
        </div>
      </div>

      {!pacienteId && <Card><CardContent className="py-12 text-center text-muted-foreground">Selecciona un paciente para ver sus certificados.</CardContent></Card>}
      {pacienteId && (
        <Card>
          <CardContent className="overflow-x-auto pt-4">
            {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Tipo</th>
                <th className="py-2 pr-3">Diagnóstico</th><th className="py-2 pr-3">Reposo</th><th className="py-2 pr-3">Médico</th>
                <th className="py-2 pr-3">Estado</th><th className="py-2"></th>
              </tr></thead>
              <tbody>
                {certs.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin certificados</td></tr>}
                {certs.map((c) => (
                  <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 font-mono font-medium">{c.codigo}</td>
                    <td className="py-2 pr-3">{new Date(c.fecha).toLocaleDateString(LOCALE)}</td>
                    <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs">{tipoLabel(c.tipo)}</span></td>
                    <td className="py-2 pr-3 text-muted-foreground">{c.diagnostico || '—'}</td>
                    <td className="py-2 pr-3 text-xs">{c.dias_reposo ? `${c.dias_reposo} día(s)` : '—'}</td>
                    <td className="py-2 pr-3">{c.medico}</td>
                    <td className="py-2 pr-3">{c.estado === 'anulado' ? <span className="text-urgente">Anulado</span> : 'Emitido'}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Imprimir" aria-label="Imprimir" onClick={() => setImprimir(c.id)}><Printer className="w-4 h-4" /></button>
                      {c.estado === 'emitido' && (
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 ml-1" title="Anular" aria-label="Anular" onClick={async () => { if (!(await dlg.confirmar(`¿Anular el certificado ${c.codigo}?`, { peligro: true }))) return; anular.mutate(c.id); }}><Ban className="w-4 h-4" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {nuevo && pacienteId && <NuevoCertificado pacienteId={pacienteId} onClose={() => setNuevo(false)} onSaved={() => { setNuevo(false); refrescar(); }} />}
      {imprimir && <CertificadoPrint id={imprimir} onClose={() => setImprimir(null)} />}
    </div>
  );
}

// ---- Nuevo certificado ----
export function NuevoCertificado({ pacienteId, onClose, onSaved, diagnosticoInicial, cie10Inicial }: {
  pacienteId: string; onClose: () => void; onSaved: () => void; diagnosticoInicial?: string; cie10Inicial?: string;
}) {
  const [tipo, setTipo] = useState('reposo');
  // Diagnóstico(s) CIE-10 con el MISMO buscador de Enfermedad actual (typeahead + modal "Códigos CIE").
  const [dx, setDx] = useState<Diagnostico[]>(
    diagnosticoInicial || cie10Inicial
      ? [{ cie10_codigo: cie10Inicial || null, descripcion: diagnosticoInicial || cie10Inicial || '', tipo: 'definitivo', es_principal: true }]
      : [],
  );
  const [cuerpo, setCuerpo] = useState('');
  const [diasReposo, setDiasReposo] = useState<number | ''>('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [contingencia, setContingencia] = useState('enfermedad_general');
  const [firmaPaciente, setFirmaPaciente] = useState('');
  // Maternidad: fecha de ingreso/nacimiento/alta + datos del recién nacido.
  const [mat, setMat] = useState({ fecha_ingreso: '', fecha_nacimiento: '', fecha_alta: '', rn_nombre: '', rn_sexo: '', rn_peso: '', rn_talla: '', adjunto_url: '' });

  const esReposo = tipo === 'reposo';
  const esMaternidad = esReposo && contingencia === 'maternidad';

  // Al fijar días + desde, calcula la fecha "hasta" automáticamente.
  const setDias = (n: number | '') => {
    setDiasReposo(n);
    if (typeof n === 'number' && desde) {
      const d = new Date(desde); d.setDate(d.getDate() + Math.max(0, n - 1));
      setHasta(d.toISOString().slice(0, 10));
    }
  };

  const guardar = useMutation({
    mutationFn: () => {
      if (esReposo) {
        if (diasReposo === '' || !desde) return Promise.reject(new Error('Para reposo indica los días y la fecha de inicio'));
        if (hasta && hasta < desde) return Promise.reject(new Error('La fecha "hasta" no puede ser anterior a "desde"'));
      }
      const prin = dx.find((d) => d.es_principal) ?? dx[0] ?? null;
      const diagnostico = dx.map((d) => d.descripcion).filter(Boolean).join('; ') || null;
      const cie10 = prin?.cie10_codigo ?? null;
      return medRpc<CertificadoDetalle>('med_crear_certificado', {
      _data: {
        paciente_id: pacienteId, tipo, diagnostico,
        cie10_codigo: cie10, cuerpo: cuerpo || null,
        dias_reposo: esReposo && diasReposo !== '' ? diasReposo : null,
        reposo_desde: esReposo ? (desde || null) : null, reposo_hasta: esReposo ? (hasta || null) : null,
        contingencia: esReposo ? contingencia : null,
        maternidad: esMaternidad
          ? Object.fromEntries(Object.entries(mat).map(([k, v]) => [k, v || null]))
          : null,
        firma_paciente_url: firmaPaciente || null,
      },
      });
    },
    onSuccess: () => { toast.success('Certificado emitido'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title="Nuevo certificado" onClose={onClose} wide>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Tipo">
            <select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value)}>
              {TIPOS_CERTIFICADO.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Campo>
        </div>
        <Campo label="Diagnóstico / motivo (CIE-10)">
          <DiagnosticosCie dx={dx} setDx={setDx} />
        </Campo>

        {esReposo && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 rounded-md border bg-muted/30 p-2">
            <Campo label="Días de reposo"><input type="number" min={1} className={fieldCls} value={diasReposo} onChange={(e) => setDias(e.target.value === '' ? '' : Number(e.target.value))} /></Campo>
            <Campo label="Desde"><input type="date" className={fieldCls} value={desde} onChange={(e) => { setDesde(e.target.value); if (typeof diasReposo === 'number' && e.target.value) { const d = new Date(e.target.value); d.setDate(d.getDate() + Math.max(0, diasReposo - 1)); setHasta(d.toISOString().slice(0, 10)); } }} /></Campo>
            <Campo label="Hasta"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
            <Campo label="Contingencia">
              <select className={fieldCls} value={contingencia} onChange={(e) => setContingencia(e.target.value)}>
                {CONTINGENCIAS_REPOSO.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
              </select>
            </Campo>
          </div>
        )}

        {esMaternidad && (
          <div className="rounded-md border border-pink-200 bg-pink-50/40 p-2 space-y-2">
            <p className="text-xs font-semibold text-pink-700">Datos de maternidad</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Campo label="Fecha de ingreso"><input type="date" className={fieldCls} value={mat.fecha_ingreso} onChange={(e) => setMat({ ...mat, fecha_ingreso: e.target.value })} /></Campo>
              <Campo label="Fecha de nacimiento"><input type="date" className={fieldCls} value={mat.fecha_nacimiento} onChange={(e) => setMat({ ...mat, fecha_nacimiento: e.target.value })} /></Campo>
              <Campo label="Fecha de alta"><input type="date" className={fieldCls} value={mat.fecha_alta} onChange={(e) => setMat({ ...mat, fecha_alta: e.target.value })} /></Campo>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Campo label="Recién nacido (nombre)"><input className={fieldCls} value={mat.rn_nombre} onChange={(e) => setMat({ ...mat, rn_nombre: e.target.value })} /></Campo>
              <Campo label="Sexo">
                <select className={fieldCls} value={mat.rn_sexo} onChange={(e) => setMat({ ...mat, rn_sexo: e.target.value })}>
                  <option value="">—</option><option value="Masculino">Masculino</option><option value="Femenino">Femenino</option>
                </select>
              </Campo>
              <Campo label="Peso (g)"><input className={fieldCls} value={mat.rn_peso} onChange={(e) => setMat({ ...mat, rn_peso: e.target.value })} placeholder="3200" /></Campo>
              <Campo label="Talla (cm)"><input className={fieldCls} value={mat.rn_talla} onChange={(e) => setMat({ ...mat, rn_talla: e.target.value })} placeholder="50" /></Campo>
            </div>
            <Campo label="Adjunto: hoja del recién nacido vivo / partida de nacimiento (URL)"><input className={fieldCls} value={mat.adjunto_url} onChange={(e) => setMat({ ...mat, adjunto_url: e.target.value })} placeholder="https://…" /></Campo>
            <p className="text-[11px] text-pink-700/80">Si la paciente ingresó antes del nacimiento, se debe emitir además otro certificado (p. ej. enfermedad general) que justifique ese período.</p>
          </div>
        )}

        <Campo label="Texto del certificado"><AutoTextarea value={cuerpo} onChange={(e) => setCuerpo(e.target.value)} placeholder="Cuerpo del certificado (se imprime tal cual)…" /></Campo>
        <div className="max-w-sm"><FirmaCanvas value={firmaPaciente} onChange={setFirmaPaciente} label="Firma del paciente (opcional)" /></div>

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Emitir certificado</Button>
        </div>
      </div>
    </Modal>
  );
}

// Fila etiqueta/valor del impreso oficial (muestra "—" si viene vacío).
function FilaDato({ k, v }: { k: string; v: string | number | null | undefined }) {
  const val = v === null || v === undefined || v === '' ? '—' : String(v);
  return (
    <tr className="align-top">
      <td className="py-0.5 pr-3 font-semibold whitespace-nowrap w-[42%]">{k}:</td>
      <td className="py-0.5">{val}</td>
    </tr>
  );
}

// ---- Impresión de certificado ----
export function CertificadoPrint({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isFetching } = useQuery<CertificadoDetalle>({ queryKey: ['med-certificado', id], queryFn: () => medRpc<CertificadoDetalle>('med_certificado', { _id: id }) });
  const { user } = useAuth();
  const c = data?.certificado;
  const esReposo = c?.tipo === 'reposo';
  const membrete = data?.empresa_logo ?? user?.empresa?.logo;
  const cont = useMemo(() => CONTINGENCIAS_REPOSO.find((x) => x.v === c?.contingencia)?.label ?? c?.contingencia, [c]);
  // Firma en el impreso: default por médico (medico_firma_modo), con override al imprimir.
  const [firmaModo, setFirmaModo] = useState<'electronica' | 'manual' | null>(null);
  const modo = firmaModo ?? (data?.medico_firma_modo === 'manual' ? 'manual' : 'electronica');
  const firmaManual = modo === 'manual';

  return (
    <Modal title="Certificado" onClose={onClose} wide>
      <style>{`@media print { body * { visibility: hidden !important; } #cert-print, #cert-print * { visibility: visible !important; } #cert-print { position: absolute; left: 0; top: 0; width: 100%; padding: 24px; } .no-print { display: none !important; } }`}</style>
      {isFetching || !data || !c ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div> : (
        <>
          <div id="cert-print" className="text-sm text-black bg-white space-y-3">
            {/* Membrete del establecimiento de salud */}
            <div className="flex items-start justify-between border-b-2 border-black pb-2">
              <div className="flex items-center gap-3">
                {membrete && <img src={membrete} alt="" className="h-16 w-16 object-contain" />}
                <div className="leading-tight">
                  <h2 className="text-base font-bold uppercase">{data.empresa}</h2>
                  {data.empresa_ruc && <p className="text-[11px]">RUC: {data.empresa_ruc}</p>}
                  {data.empresa_direccion && <p className="text-[11px]">{data.empresa_direccion}</p>}
                  {(data.empresa_telefono || data.empresa_email) && (
                    <p className="text-[11px]">
                      {data.empresa_telefono && <>Tel: {data.empresa_telefono}</>}
                      {data.empresa_telefono && data.empresa_email && ' · '}
                      {data.empresa_email}
                    </p>
                  )}
                </div>
              </div>
              <QRCodeSVG value={`CERT:${c.codigo}:${c.codigo_verificacion}`} size={78} />
            </div>

            <div className="text-center">
              <h3 className="text-base font-bold uppercase">Certificado médico de {tipoLabel(c.tipo)}</h3>
              <p className="text-[11px]">Nº {c.codigo}{c.estado === 'anulado' ? ' (ANULADO)' : ''} · {new Date(c.fecha).toLocaleString(LOCALE)}</p>
            </div>

            {esReposo ? (<>
              {/* Datos del paciente — orden oficial */}
              <section>
                <h4 className="text-xs font-bold uppercase border-b mb-1">Datos del paciente</h4>
                <table className="w-full text-[11px]"><tbody>
                  <tr className="align-top">
                    <td className="py-0.5 pr-3 font-semibold whitespace-nowrap w-[42%]">Apellidos y nombres:</td>
                    <td className="py-0.5"><span className="flex flex-wrap items-center gap-x-2 gap-y-1"><span>{data.paciente}</span><MisionBadge codigo={data.paciente_mision_codigo} nombre={data.paciente_mision} /></span></td>
                  </tr>
                  <FilaDato k="Dirección domiciliaria" v={data.paciente_direccion} />
                  <FilaDato k="Teléfono de contacto" v={data.paciente_telefono} />
                  <FilaDato k="Institución / empresa donde trabaja" v={data.paciente_institucion} />
                  <FilaDato k="Puesto de trabajo" v={data.paciente_puesto} />
                  <FilaDato k="Cédula de identidad / pasaporte" v={data.paciente_ident} />
                  <FilaDato k="Nº de historia clínica" v={data.paciente_hc} />
                </tbody></table>
              </section>

              {/* Motivos de enfermedad */}
              <section>
                <h4 className="text-xs font-bold uppercase border-b mb-1">Motivos</h4>
                <table className="w-full text-[11px]"><tbody>
                  <FilaDato k="Diagnóstico (CIE-10)" v={c.diagnostico} />
                  <FilaDato k="Código CIE-10" v={c.cie10_codigo} />
                  <FilaDato k="Tipo de contingencia" v={cont} />
                  <FilaDato k="Total de días concedidos" v={c.dias_reposo != null ? cantidadEnLetras(String(c.dias_reposo)) : '—'} />
                  <FilaDato k="Desde (día/mes/año)" v={fechaEnLetras(c.reposo_desde)} />
                  <FilaDato k="Hasta (día/mes/año)" v={fechaEnLetras(c.reposo_hasta)} />
                </tbody></table>
              </section>

              {/* Maternidad */}
              {c.contingencia === 'maternidad' && c.maternidad && (
                <section>
                  <h4 className="text-xs font-bold uppercase border-b mb-1">Maternidad</h4>
                  <table className="w-full text-[11px]"><tbody>
                    <FilaDato k="Fecha de ingreso" v={fechaEnLetras(c.maternidad.fecha_ingreso)} />
                    <FilaDato k="Fecha de nacimiento" v={fechaEnLetras(c.maternidad.fecha_nacimiento)} />
                    <FilaDato k="Fecha de alta" v={fechaEnLetras(c.maternidad.fecha_alta)} />
                    <FilaDato k="Recién nacido" v={[c.maternidad.rn_nombre, c.maternidad.rn_sexo].filter(Boolean).join(' · ')} />
                    <FilaDato k="Peso / talla" v={[c.maternidad.rn_peso && `${c.maternidad.rn_peso} g`, c.maternidad.rn_talla && `${c.maternidad.rn_talla} cm`].filter(Boolean).join(' · ')} />
                  </tbody></table>
                  <p className="mt-1 text-[10px] text-gray-600">Se adjunta la hoja del recién nacido vivo o la partida de nacimiento. Si la paciente ingresó antes del nacimiento, se justifica con un certificado adicional.</p>
                </section>
              )}
            </>) : (
              <div className="text-xs space-y-1">
                <p className="flex flex-wrap items-center gap-x-2 gap-y-1"><span><b>Paciente:</b> {data.paciente} ({data.paciente_ident ?? 's/i'}){data.paciente_edad != null ? ` · ${data.paciente_edad} años` : ''}</span><MisionBadge codigo={data.paciente_mision_codigo} nombre={data.paciente_mision} /></p>
                {c.diagnostico && <p><b>Diagnóstico:</b> {c.diagnostico}{c.cie10_codigo ? ` (CIE-10: ${c.cie10_codigo})` : ''}</p>}
              </div>
            )}

            {c.cuerpo && <p className="text-sm whitespace-pre-wrap leading-relaxed">{c.cuerpo}</p>}

            {/* Firma de responsabilidad del profesional emisor */}
            <div className="flex justify-around pt-10 text-center text-[11px]">
              <div>
                {firmaManual || !data.medico_firma
                  ? <div className="h-12" />
                  : <img src={data.medico_firma} alt="firma médico" className="mx-auto h-12 object-contain" />}
                <div className="mt-1 border-t px-6 pt-1 leading-tight">
                  <div className="font-semibold">{data.medico}</div>
                  {data.medico_cedula && <div>C.I. {data.medico_cedula}</div>}
                  {data.especialidad && <div>{data.especialidad}</div>}
                  {data.medico_licencia && <div>Lic./Reg. {data.medico_licencia}</div>}
                </div>
              </div>
              {c.firma_paciente_url && (
                <div>
                  <img src={c.firma_paciente_url} alt="firma paciente" className="mx-auto h-12 object-contain" />
                  <div className="mt-1 border-t px-6 pt-1">{data.paciente}</div>
                </div>
              )}
            </div>
          </div>
          <div className="no-print flex items-center justify-end gap-3 pt-3 border-t mt-3">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              Firma:
              <select value={modo} onChange={(e) => setFirmaModo(e.target.value as 'electronica' | 'manual')} className="rounded border px-2 py-1 text-sm">
                <option value="electronica">Electrónica</option>
                <option value="manual">Manual (firma a mano)</option>
              </select>
            </label>
            <Button variant="outline" onClick={onClose}>Cerrar</Button>
            <Button onClick={() => window.print()}><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
          </div>
        </>
      )}
    </Modal>
  );
}
